import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useModel } from "@/contexts/ModelContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useCurrentMedia } from "@/store";
import { useCurrentWorkflow } from "@/hooks/useCurrentWorkflow";
import { LoadingOverlay } from "@/components/file/Preview/Loading";
import { useDownloadAnnotatedFile } from "@/hooks/useDownloadAnnotatedFile";
import { useShareWorkflow } from "@/hooks/useShareWorkflow";
import { useWorkflowObjectName } from "@/hooks/useWorkflowObjectName";
import { Chip } from "@scripts/react/components/Chip";
import { EmbeddedWorkflowActions } from "./EmbeddedWorkflowActions";
import { FileCarousel, FileCarouselLayout } from "@/components/file/Carousel/FileCarousel";
import { UploadLimitErrorType } from "@/constants/media";

interface EmbeddedWorkflowPreviewProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    isHovered?: boolean;
}

export const EmbeddedWorkflowPreview: React.FC<EmbeddedWorkflowPreviewProps> = ({
    children,
    className,
    onAddMediaItems,
    uploadLimitError,
    isHovered
}) => {
    const { isDownloading } = useModel();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const currentMedia = useCurrentMedia();
    const handleDownloadAnnotatedFileClick = useDownloadAnnotatedFile({
        watermarkUrl: "https://rapid.roboflow.com"
    });
    const { shareUrl, copied, handleShare: handleShareClick } = useShareWorkflow();
    const { objectNames } = useWorkflowObjectName();

    return (
        <div
            className={twClassNames(
                "flex h-full w-full flex-col overflow-hidden rounded-lg border border-divider bg-white shadow-lg",
                className
            )}
        >
            <div className="flex flex-col border-b border-divider px-4 py-3 text-base font-medium md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                    <img src="/images/logo.svg" alt="Logo" className="h-auto w-5" />
                    <span className="text-tertiary">Try Out My Model</span>
                    <span className="text-primary">{workflow?.name}</span>
                    {objectNames?.map((obj, index) => (
                        <Chip key={index} color="gray" type="text" size="small">
                            {obj}
                        </Chip>
                    ))}
                </div>
                <div className="hidden md:block">
                    <EmbeddedWorkflowActions
                        onDownloadClick={handleDownloadAnnotatedFileClick}
                        onShareClick={handleShareClick}
                        isDownloading={isDownloading}
                        shareUrl={shareUrl}
                        copied={copied}
                    />
                </div>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key="with-files"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="flex min-h-0 flex-1 flex-col"
                >
                    <FileCarousel
                        layout={FileCarouselLayout.SHARE}
                        onAddMediaItems={onAddMediaItems}
                        uploadLimitError={uploadLimitError}
                        className="min-h-0 flex-1"
                        isHovered={isHovered}
                    >
                        <>
                            {children}
                            <LoadingOverlay
                                show={isDownloading}
                                loadingText={
                                    currentMedia?.file.isVideo
                                        ? "Processing video..."
                                        : "Preparing download..."
                                }
                            />
                        </>
                    </FileCarousel>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
